import * as React from "react";
import { useNavigate } from "react-router";
import createIOSortType from "../../../../lib/createIOSortType";
import { HIDE_ON_EXPORT } from "@ui/lib/downloadPage";
import createGlobalState from "@ui/lib/createGlobalState";
import { useModals } from "@ui/lib/hooks/useModal";
import useSortedData from "../../../../lib/hooks/useSortedData";
import t from "../../../../i18n";
import type { SortableValue } from "diagnostics-lib/comparator";
import Table from "@ui/Table";
import TableCard from "@ui/Table/Card";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import type { SetupModalType } from "../SetupModalType";
import Modal from "@ui/Modal";
import DeleteModalForm from "./DeleteModalForm";
import CreateUpdateModalForm from "./CreateUpdateModalForm";
import { styled } from "@linaria/react";
import Actions from "./Actions";
import Ellipsis from "@ui/Ellipsis";
import formatDate from "@ui/i18n/formatDate";
import type { Setup } from "../../../../api/useSetups";
import { computeEndDate, GlobalDiagnosisStatus, SimulationStatus } from "../../../../api/useSetups";
import { useCanUserDoOnProject } from "../../../canUserDo";
import LabelledHint from "./StatusDisplay/LabelledHint";
import { mapStatusDisplay, StatusDisplay } from "./StatusDisplay/utils";
import Details from "@ui/Details";
import DuplicateModalForm from "./DuplicateModalForm";
import type { Project } from "../../../../api/useProjects";
import useActiveAnalysesStream, {
  getSetupActiveAnalysisCount,
} from "../../../../api/useActiveAnalysesStream";
import { toPath } from "../../../../router/helpers/path";
import { toBasenamePath } from "../../../../router/helpers/navigation";

const StyledActions = styled(Actions)`
  display: none;
  margin-left: auto;
  tr:hover & {
    display: flex;
  }
`;

const columns = [
  "name",
  "description",
  "author",
  "startDate",
  "endDate",
  "analysisCount",
  "status",
] as const;
type Column = (typeof columns)[number];

const getLabel = (column: Column) => {
  switch (column) {
    case "name":
      return t("setups.table.column.name");
    case "description":
      return t("setups.table.column.description");
    case "author":
      return t("setups.table.column.author");
    case "startDate":
      return t("setups.table.column.startDate");
    case "endDate":
      return t("setups.table.column.endDate");
    case "analysisCount":
      return t("setups.table.column.analysisCount");
    case "status":
      return t("setups.table.column.status");
    default:
      throw new Error(column satisfies never);
  }
};

const computeRowKey = (data: Setup) => `setup-id-${data.id}`;

const useSort = createGlobalState({
  storage: "session",
  key: "setupsSort",
  type: createIOSortType(columns),
  defaultValue: {
    column: "endDate",
    direction: "asc",
  },
  scoped: true,
});

interface P {
  setups: Setup[];
  project: Project;
  timeZone: string;
}

const simulationHintStatus = mapStatusDisplay<SimulationStatus>([
  [SimulationStatus.VALIDATING, StatusDisplay.WARNING],
  [SimulationStatus.VALID, StatusDisplay.SUCCESS],
  [SimulationStatus.INCOMPLETE, StatusDisplay.PENDING],
  [SimulationStatus.INVALID, StatusDisplay.ERROR],
]);

const diagnosisHintStatus = mapStatusDisplay<GlobalDiagnosisStatus>([
  [GlobalDiagnosisStatus.COMPLETE, StatusDisplay.SUCCESS],
  [GlobalDiagnosisStatus.INCOMPLETE, StatusDisplay.PENDING],
  [GlobalDiagnosisStatus.INVALID, StatusDisplay.ERROR],
]);

type Data = Setup & { activeAnalysisCount: number };

const SetupsTable = ({ setups, project, timeZone }: P) => {
  const [sort, setSort] = useSort({ scope: project.id });
  const canEditSetup = useCanUserDoOnProject("PROJECT_SETUP", project.id);
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const sortSelector: {
    [key in Column]: (p: Data) => SortableValue;
  } = React.useMemo(
    () => ({
      name: s => s.name,
      description: s => s.description || "",
      author: s => s.author.name || "",
      startDate: s => s.startDate || "",
      endDate: s => computeEndDate(s, setups) || "",
      analysisCount: s => s.activeAnalysisCount || "",
      status: s => s.isPublished || "",
    }),
    [setups],
  );
  const [setup, setSetup] = React.useState<Setup>();

  const { modal, closeModal, openModal } = useModals<SetupModalType>();

  const openSetupModal = React.useCallback(
    (modalType: SetupModalType, setup: Setup) => {
      setSetup(setup);
      openModal(modalType);
    },
    [openModal],
  );

  const openEditModal = React.useCallback(
    (setup: Setup) => (e: React.MouseEvent) => {
      e.stopPropagation();
      openSetupModal("update", setup);
    },
    [openSetupModal],
  );

  const openDuplicateModal = React.useCallback(
    (setup: Setup) => (e: React.MouseEvent) => {
      e.stopPropagation();
      openSetupModal("duplicate", setup);
    },
    [openSetupModal],
  );

  const openDeleteModal = React.useCallback(
    (setup: Setup) => (e: React.MouseEvent) => {
      e.stopPropagation();
      openSetupModal("delete", setup);
    },
    [openSetupModal],
  );

  const metadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<Data> } = {
      name: {
        label: getLabel("name"),
        render: s => (
          <>
            <Ellipsis>{s.name}</Ellipsis>
            {canEditSetup && (
              <StyledActions
                openEditModal={openEditModal(s)}
                openDuplicateModal={openDuplicateModal(s)}
                openDeleteModal={openDeleteModal(s)}
                className={HIDE_ON_EXPORT}
              />
            )}
          </>
        ),
        sticky: true,
        minWidth: "150px",
        maxWidth: "180px",
        sortable: true,
      },
      description: {
        label: getLabel("description"),
        render: s => (s.description === null ? undefined : <Details text={s.description} />),
        minWidth: "200px",
        sortable: true,
      },
      author: {
        label: getLabel("author"),
        render: s => <Ellipsis>{s.author.name ?? s.author.id}</Ellipsis>,
        minWidth: "200px",
        maxWidth: "250px",
        sortable: true,
      },
      startDate: {
        label: getLabel("startDate"),
        render: s => (
          <Ellipsis
            title={
              s.startDate === null
                ? undefined
                : formatDate(new Date(s.startDate), "datetime", timeZone)
            }
          >
            {s.startDate === null ? null : formatDate(new Date(s.startDate), "date", timeZone)}
          </Ellipsis>
        ),
        minWidth: "150px",
        maxWidth: "250px",
        sortable: true,
      },
      endDate: {
        label: getLabel("endDate"),
        render: s => (
          <Ellipsis
            title={
              computeEndDate(s, setups) === null
                ? undefined
                : formatDate(new Date(computeEndDate(s, setups)!), "datetime", timeZone)
            }
          >
            {computeEndDate(s, setups) === null
              ? null
              : formatDate(new Date(computeEndDate(s, setups)!), "date", timeZone)}
          </Ellipsis>
        ),
        minWidth: "150px",
        maxWidth: "250px",
        sortable: true,
      },
      analysisCount: {
        label: getLabel("analysisCount"),
        render: s => s.activeAnalysisCount,
        minWidth: "180px",
        maxWidth: "180px",
        sortable: true,
      },
      status: {
        label: getLabel("status"),
        render: s => (
          <div>
            <LabelledHint
              status={simulationHintStatus(s.simulationStatus)}
              label={t("setup.header.nav.simulation")}
            />
            <LabelledHint
              status={diagnosisHintStatus(s.diagnosisStatus)}
              label={t("setup.header.nav.diagnosis")}
            />
          </div>
        ),
        minWidth: "120px",
        maxWidth: "120px",
        sortable: true,
      },
    };
    return _;
  }, [canEditSetup, openDeleteModal, openDuplicateModal, openEditModal, setups, timeZone]);

  const data = React.useMemo(
    () =>
      setups.map(setup => ({
        ...setup,
        activeAnalysisCount: getSetupActiveAnalysisCount(setup, setups, activeAnalyses),
      })),
    [activeAnalyses, setups],
  );

  const sortedSetups = useSortedData(data, sortSelector[sort.column], sort.direction);

  const navigate = useNavigate();
  const gotoSetup = React.useCallback(
    (setup: Setup, e: React.MouseEvent<HTMLTableRowElement>) => {
      const targetPath = toPath("/projects/:projectId/setup/:setupId", {
        projectId: project.id,
        setupId: setup.id,
      });
      if (e.metaKey || e.ctrlKey) {
        window.open(toBasenamePath(targetPath));
      } else {
        void navigate(targetPath);
      }
    },
    [navigate, project.id],
  );

  return (
    <>
      {setup && (
        <>
          <Modal
            title={t("setups.modal.delete_setup")}
            isOpen={modal === "delete"}
            onRequestClose={closeModal}
          >
            <DeleteModalForm
              setup={setup}
              setups={setups}
              project={project}
              onRequestClose={closeModal}
            />
          </Modal>
          <Modal
            title={t("setups.modal.duplicate_setup")}
            isOpen={modal === "duplicate"}
            onRequestClose={closeModal}
          >
            <DuplicateModalForm setup={setup} projectId={project.id} onRequestClose={closeModal} />
          </Modal>
          <Modal
            title={t("setups.modal.add_or_edit.create_setup.update")}
            isOpen={modal === "update"}
            onRequestClose={closeModal}
          >
            <CreateUpdateModalForm
              setups={setups}
              setup={setup}
              projectId={project.id}
              onRequestClose={closeModal}
              timeZone={timeZone}
              okButtonName={t("modalForm.button.name.edit")}
            />
          </Modal>
        </>
      )}
      <TableCard title={null} headerLeft={null} headerRight={null}>
        <Table
          stickyHeader
          datas={sortedSetups}
          metadata={metadata}
          computeRowKey={computeRowKey}
          sort={sort}
          onSort={setSort}
          onRowClick={gotoSetup}
          data-cy="setups-table"
        />
      </TableCard>
    </>
  );
};
export default React.memo(SetupsTable);
